import { HttpParams } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AnalyticsService,
  ModalService,
  SearchComponent,
  SearchServiceToken,
  SimpleModalConfig,
} from '@frk/eds-components';
import {
  FundsSearchResponse,
  SearchFundsItem,
} from '@marketing/subscriptions-manage/subscription-manage.interface';
import { FavoritesService } from '@products/services/favorites.service';
import {
  FtSearchOptions,
  FtSearchParams,
  FtSearchResponse,
} from '@search/interfaces/search.interface';
import { ConfigService } from '@search/services/config.service';
import { FtSearchService } from '@search/services/ftsearch.service';
import { SiteConfigService } from '@services';
import {
  FavoritesRequestDetail,
  FundShareClassId,
  ProductData,
  ShareClassName,
} from '@types';
import { Logger } from '@utils/logger';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { USServicingService } from '../services/us-servicing.service';

const logger = Logger.getLogger('AddtoWatchlistComponent');
const FUNDS = 'funds';

@Component({
  selector: 'ft-add-to-watchlist-modal',
  templateUrl: './add-to-watchlist-modal.component.html',
})
export class AddToWatchlistModalComponent implements OnInit, OnDestroy {
  public isVisible$: Observable<boolean>;
  public modalConfig: SimpleModalConfig;
  public showModal = false;
  @ViewChild('onPageSearch', { static: false }) onPageSearch: SearchComponent;
  private componentDestroyed$: Subject<boolean> = new Subject();
  public searchItems: SearchFundsItem[] = [];
  public hasError = false;
  public isSearchEmpty = false;
  private debouncer$: Subject<FtSearchParams> = new Subject<FtSearchParams>();
  private page = 0;
  public isViewMoreBtnVisible = true;
  public isProductAlreadyExist = false;
  private searchOptions: FtSearchOptions = {
    page: 0,
    term: '',
    dataSource: 'dev',
    config: {
      ftSearchUrl: '',
      ftInsightsUrl: '',
      ftAutoCompleteUrl: '',
      httpParams: new HttpParams(),
      httpOptions: {},
    },
    counters: {
      funds: 100,
      literature: 4,
      pages: 4,
    },
  };

  @Input() brLabels: object = {};
  constructor(
    private configService: ConfigService,
    private cd: ChangeDetectorRef,
    private usServicingService: USServicingService,
    private modalService: ModalService,
    private favoriteService: FavoritesService,
    private analyticsService: AnalyticsService,
    @Inject(SearchServiceToken) private searchService: FtSearchService,
    private siteConfigService: SiteConfigService
  ) {
    this.searchService.onPageSearchOutput$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        ({ results, error }) => {
          // error handling without unsubscribing from observable
          if (error) {
            this.hasError = true;
            logger.debug('error in search response');
            return;
          }
          const fundResults: FundsSearchResponse = this.getFundsResults(
            results
          ) as any;
          if (fundResults) {
            // filtering the active funds
            const activeFunds = fundResults.response?.hits?.hits.filter(
              (item) =>
                this.siteConfigService.isActiveShareClass(
                  `${item._source.fundid}-${item._source.shclcode}` as FundShareClassId
                ) ||
                this.siteConfigService.isSoftLaunchShareClass(
                  `${item._source.fundid}-${item._source.shclcode}` as FundShareClassId
                )
            );

            this.isViewMoreBtnVisible =
              fundResults.response?.hits?.hits?.length === 100;

            if (this.page === 0) {
              this.searchItems = this.usServicingService.getFundsFromResult(
                activeFunds
              );
              this.isProductAlreadyExist =
                activeFunds.length > 0 && this.searchItems?.length === 0;
            } else {
              this.searchItems = [
                ...this.searchItems,
                ...this.usServicingService.getFundsFromResult(activeFunds),
              ];
            }

            this.isSearchEmpty = this.searchItems?.length === 0 || false;

            logger.debug('search result from elastic', this.searchItems);
          }
          this.cd.markForCheck();
        },
        () => {
          this.hasError = true;
        }
      );

    // ignore search term if new is called within 500ms
    // debouncer is triggered after search term
    this.debouncer$
      .pipe(takeUntil(this.componentDestroyed$), debounceTime(500))
      .subscribe((searchParams: FtSearchParams) =>
        this.searchService.findAFund(searchParams)
      );
  }

  ngOnInit(): void {
    this.modalConfig = {
      modalId: 'watchlistModal',
      type: 'simple',
    };
    this.configService
      .getConfig('')
      ?.pipe(takeUntil(this.componentDestroyed$))
      .subscribe((config) => {
        this.searchOptions.config = config;
      });
  }

  /**
   * Get Funds Results
   * In some cases autoComplete search returns array. For US region we need to get results only from FUNDS record.
   * @param results - FtSearchResponse or FtSearchResponse array
   * @returns - FtSearchResponse for FUNDS
   */
  private getFundsResults(
    results: FtSearchResponse[] | FtSearchResponse
  ): FtSearchResponse {
    if (!Array.isArray(results)) {
      return results;
    }
    return results.find((result: FtSearchResponse) => result.name === 'FUNDS');
  }

  /**
   * search entered string
   */
  public findAFund(term: string) {
    if (term === '') {
      this.onClearResults();
    } else {
      this.hasError = false;
      this.isSearchEmpty = false;
      this.isProductAlreadyExist = false;
      this.searchOptions.term = term;
      this.searchOptions.page = this.page;
      this.searchOptions.collection = FUNDS;

      const searchParams: FtSearchParams = {
        options: this.searchOptions,
        filters: JSON.stringify([
          {
            fieldName: 'producttype.exact',
            fieldValue: this.siteConfigService.getUSServicingProducts(),
          },
        ]),
      };

      this.debouncer$.next(searchParams);
    }
  }

  /**
   * add to watchlist
   */
  /**
   * add to watchlist
   */
  public addToWatchlist(
    fund: SearchFundsItem,
    fundShareClassId: FundShareClassId,
    shareClass: ShareClassName
  ) {
    // track UA and GA4 event in the single event for add focus list
    this.analyticsService.trackEvent({
      event: 'add_product_list',
      detailed_event: 'Add Product List',
      fund_name: fund.fundName,
      event_data: {
        cart_type: 'focus list',
        fund_class: shareClass,
        fund_name: fund.fundName,
        fund_number: Number(fund.fundId),
        fund_ticker: fund.ticker,
      },
    });
    // get the fund data for additional details needed to save fav.
    this.usServicingService
      .getProductDataForFavorite$(fundShareClassId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((fundData: ProductData) => {
        this.favoriteService.toggleFavorite(
          this.getFavoriteFundDetail(fundData, fundShareClassId)
        );
        this.showModal = false;
        this.onClearResults();
        this.modalService.close(this.modalConfig.modalId);
      });
  }

  // get detail for fund to remove or add to favorites
  private getFavoriteFundDetail = (
    fundData: ProductData,
    fundShareClassId: FundShareClassId
  ): FavoritesRequestDetail => ({
    fundName: fundData.fundName,
    productType: fundData.shareClass.productType,
    fundUrl: fundData.shareClass.link,
    fundShareClassId,
  });

  ngOnDestroy(): void {
    // unsubscribe from Observables
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  /**
   * on closing the modal
   */
  public handleCloseModal() {
    this.onClearResults();
    this.showModal = false;
  }

  /**
   * clear the searched string
   */
  private onClearResults() {
    this.searchOptions.term = '';
    this.searchItems = [];
    this.onPageSearch.value = '';
    this.onPageSearch.hasTyped = false;
    this.isSearchEmpty = false;
    this.isProductAlreadyExist = false;
    this.hasError = false;
    this.page = 0;
  }
  /**
   * we want to load more funds and
   * to call search api
   */
  public viewMore() {
    this.page += 1;
    this.findAFund(this.searchOptions.term);
  }
}
